import React from 'react';
import { Box, Paper, Typography, useTheme, Divider } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTranslation } from 'react-i18next';
import { TaxReportSummary as TaxReportSummaryData, TaxReportSummaryGroup } from '../../../services/tax-reports.types';
import { TransactionIssueType } from '../../../services/transaction.types';
import SkeletonWithTooltip from './SkeletonWithTooltip';

interface TaxSummaryBoxProps {
  data: TaxReportSummaryData[];
  isPaid: boolean;
  savingsPotential: number;
  onNavigateToTransactions: (issueType: TransactionIssueType) => void;
}

const TaxSummaryBox: React.FC<TaxSummaryBoxProps> = ({ 
  data, 
  isPaid, 
  savingsPotential,
  onNavigateToTransactions 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Calculate the total taxable amount
  const calculateTaxableAmount = (): number => {
    if (!data || data.length === 0) return 0;

    // Helper function to find a value in the nested structure
    const findValueByLabel = (group: TaxReportSummaryGroup, targetLabel: string): number => {
      // Check direct entries
      for (const entry of group.entries || []) {
        if (entry.kv && entry.kv.label === targetLabel) {
          return entry.kv.value;
        }
        // Recursively check nested groups
        if (entry.g) {
          const nestedValue = findValueByLabel(entry.g, targetLabel);
          if (nestedValue !== 0) {
            return nestedValue;
          }
        }
      }
      return 0;
    };

    let totalAmount = 0;
    
    // Process each summary data item
    for (const summaryData of data) {
      if (summaryData.g) {
        // Find values for specific income types
        const cryptoSalesGains = findValueByLabel(
          summaryData.g, 
          'Einkünfte aus privaten Veräußerungsgeschäften nach § 23 EStG'
        );
        
        const otherIncome = findValueByLabel(
          summaryData.g, 
          'Sonstige Einkünfte nach § 22 Nr. 3 EStG'
        );
        
        const marginDerivativesGains = findValueByLabel(
          summaryData.g, 
          'Kapitalerträge aus Margin- und Derivategeschäften'
        );

        // Sum up all taxable income
        totalAmount += cryptoSalesGains + otherIncome + marginDerivativesGains;
      }
    }
    
    return totalAmount;
  };

  // Format the amount as currency
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const taxableAmount = calculateTaxableAmount();

  // Determine the left side content based on taxable amount
  const renderLeftSide = () => {
    if (isPaid && taxableAmount === 0) {
      // Case 1: No taxes to pay
      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            justifyContent: 'center'
          }}>
            <CheckCircleIcon 
              sx={{ 
                color: theme.palette.grey[600], 
                fontSize: '2rem',
                mr: 1.5
              }} 
            />
            <Typography variant="body1" fontWeight="bold" fontSize="1.25rem" color="text.secondary">
              {t('taxReports.summary.noTaxesToPay', 'Super! Keine Steuern zu zahlen')}
            </Typography>
          </Box>

          {isPaid ? (
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              sx={{ 
                color: theme.palette.grey[600],
                fontSize: '2rem',
                mb: 1
              }}
            >
              {formatAmount(0)}
            </Typography>
          ) : (
            <SkeletonWithTooltip 
              width={120} 
              height={40}
              sx={{ 
                mb: 1,
                mx: 'auto'
              }}
            />
          )}
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: '0.85rem',
              maxWidth: '90%'
            }}
          >
            {t('taxReports.summary.taxableIncome', 'Summe aller steuerpflichtigen Einkünfte')}
          </Typography>
        </Box>
      );
    } else if (taxableAmount < 0) {
      // Case 2: Tax loss
      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            justifyContent: 'center'
          }}>
            <TrendingDownIcon 
              sx={{ 
                color: '#4caf50', 
                fontSize: '2rem',
                mr: 1.5
              }} 
            />
            <Typography variant="body1" fontWeight="bold" fontSize="1.25rem" color="text.secondary">
              {t('taxReports.summary.taxLoss', 'Du hast Verluste')}
            </Typography>
          </Box>

          {isPaid ? (
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              sx={{ 
                color: '#4caf50',
                fontSize: '2rem',
                mb: 1
              }}
            >
              {formatAmount(taxableAmount)}
            </Typography>
          ) : (
            <SkeletonWithTooltip 
              width={120} 
              height={40}
              sx={{ 
                mb: 1,
                mx: 'auto'
              }}
            />
          )}
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: '0.85rem',
              maxWidth: '90%'
            }}
          >
            {t('taxReports.summary.taxLossExplanation', 'Du könntest Anspruch auf eine Steuerrückerstattung haben')}
          </Typography>
        </Box>
      );
    } else {
      // Case 3: Normal taxable profit
      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            justifyContent: 'center'
          }}>
            <AccountBalanceIcon 
              sx={{ 
                color: theme.palette.primary.main, 
                fontSize: '2rem',
                mr: 1.5
              }} 
            />
            <Typography variant="body1" fontWeight="bold" fontSize="1.25rem" color="text.secondary">
              {t('taxReports.summary.yourTaxableProfit', 'Dein steuerpflichtiger Gewinn')}
            </Typography>
          </Box>

          {isPaid ? (
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              sx={{ 
                color: theme.palette.primary.main,
                fontSize: '2rem',
                mb: 1
              }}
            >
              {formatAmount(taxableAmount)}
            </Typography>
          ) : (
            <SkeletonWithTooltip 
              width={120} 
              height={40}
              sx={{ 
                mb: 1,
                mx: 'auto'
              }}
            />
          )}
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: '0.85rem',
              maxWidth: '90%'
            }}
          >
            {t('taxReports.summary.taxableIncome', 'Taxable income')}
          </Typography>
        </Box>
      );
    }
  };

  // Determine the right side content based on savings potential
  const renderRightSide = () => {
    if (savingsPotential <= 0) {
      // No savings potential
      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          textAlign: 'center',
          justifyContent: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            justifyContent: 'center'
          }}>
            <CheckCircleIcon 
              sx={{ 
                color: theme.palette.text.secondary, 
                fontSize: '2rem',
                mr: 1.5
              }} 
            />
            <Typography variant="body1" fontWeight="bold" fontSize="1.25rem" color="text.secondary">
              {t('taxReports.summary.noSavingsPotential', 'Deine Steuerdaten sehen gut aus')}
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: '0.85rem',
              maxWidth: '90%',
              mt: 1
            }}
          >
            {t('taxReports.summary.noSavingsPotentialExplanation', 'Wir konnten keine Steuer-Sparpotenziale identifizieren')}
          </Typography>
        </Box>
      );
    } else {
      // Normal savings potential
      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            justifyContent: 'center'
          }}>
            <SavingsIcon 
              sx={{ 
                color: '#4caf50', 
                fontSize: '2rem',
                mr: 1.5
              }} 
            />
            <Typography variant="body1" fontWeight="bold" fontSize="1.25rem" color="text.secondary">
              {t('taxReports.costBasis.savingPotential', 'Dein Steuer-Sparpotenzial')}
            </Typography>
          </Box>

            <Typography 
              variant="h4" 
              fontWeight="bold" 
              sx={{ 
                color: '#4caf50',
                fontSize: '2rem',
                mb: 1
              }}
            >
              {formatAmount(savingsPotential)}
            </Typography>

          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: 1,
              backgroundColor: '#4caf50',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: '#3d8b40',
              },
              mt: { xs: 1, sm: 2 }
            }}
            onClick={() => onNavigateToTransactions(TransactionIssueType.MissingCostBasis)}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '0.9rem',
                color: 'white',
              }}
            >
              {t('taxReports.costBasis.checkTransactions', 'Prüfe Transaktionen')}
            </Typography>
            <ArrowForwardIcon 
              sx={{ 
                ml: 0.5, 
                fontSize: '0.9rem',
                color: 'white'
              }} 
            />
          </Box>
        </Box>
      );
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Main container with responsive layout */}
      <Box sx={{ 
        display: 'flex',
        width: '100%',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        {/* Left side - Tax amount section with conditional rendering */}
        <Box sx={{ 
          width: savingsPotential > 0 ? { xs: '100%', sm: '50%' } : '100%',  // 50% width when savings potential exists, otherwise full width
          mb: { xs: 3, sm: 0 }
        }}>
          {renderLeftSide()}
        </Box>

        {/* Only show right side and dividers if there's savings potential */}
        {savingsPotential > 0 && (
          <>
            {/* Vertical divider - only visible on larger screens */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            
            {/* Horizontal divider - only visible on mobile */}
            <Divider sx={{ width: '100%', my: 2, display: { xs: 'block', sm: 'none' } }} />

            {/* Right side - Savings potential section with conditional rendering */}
            <Box sx={{ 
              width: { xs: '100%', sm: '50%' }
            }}>
              {renderRightSide()}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default TaxSummaryBox;
